import React from 'react';
import styles from '../AdminOnboardingUsersTable.module.css';

const ACTION_ICONS = {
  dashboard: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
  chart: 'M7 20h2V8H7v12zm4 0h2V4h-2v16zm4 0h2v-8h-2v8z',
  detail: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  delete: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
};

function ActionIcon({ name }) {
  return (
    <svg aria-hidden="true" className={styles['btn-icon']} viewBox="0 0 24 24" fill="currentColor">
      <path d={ACTION_ICONS[name]} />
    </svg>
  );
}

function SortColumnIcon({ columnKey, sortConfig }) {
  if (sortConfig.key !== columnKey) {
    return <span className={styles['sort-icon']}>⇅</span>;
  }
  return (
    <span className={styles['sort-icon-active']}>
      {sortConfig.direction === 'asc' ? '↑' : '↓'}
    </span>
  );
}

function SortableHeader({ columnKey, label, extraClass, sortConfig, onSort }) {
  return (
    <th scope="col" className={`${styles.sortableTh} ${styles[`${columnKey}-header`]}`}>
      <button
        type="button"
        className={`${styles['sortable-header']} ${styles[extraClass]}`}
        onClick={() => onSort(columnKey)}
      >
        <span className={styles['header-content']}>
          {label}
          <SortColumnIcon columnKey={columnKey} sortConfig={sortConfig} />
        </span>
      </button>
    </th>
  );
}

const UsersTable = React.memo(function UsersTable({
  users,
  searchTerm,
  onSearchChange,
  onClearSearch,
  filteredAndSortedUsers,
  sortConfig,
  onSort,
  isChartLoading,
  onDashboardClick,
  onChartDetailClick,
  onDetailClick,
  onDeleteUserClick,
  isSuperadmin,
  deleteSubmittingUsername,
}) {
  return (
    <>
      <div className={styles['table-header-section']}>
        <h2 className={styles['table-title']}>
          <svg aria-hidden="true" className={styles['title-icon']} viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </svg>
          Onboarding Users
        </h2>
        <span className={styles['users-count-badge']}>
          {users.length} {users.length === 1 ? 'user' : 'users'}
        </span>
      </div>
      <div className={styles['table-controls']}>
        <div className={styles['search-container']}>
          <span className={styles['search-icon']} aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </span>
          <input
            type="text"
            className={styles['search-input']}
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={onSearchChange}
          />
          {searchTerm ? (
            <button
              type="button"
              className={styles['clear-search']}
              onClick={onClearSearch}
              title="Clear search"
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>
        <div className={styles['results-count']}>
          Showing <strong>{filteredAndSortedUsers.length}</strong> of <strong>{users.length}</strong> users
        </div>
      </div>

      {users.length === 0 ? (
        <div className={styles['no-data']}>
          <p>No onboarding users found.</p>
        </div>
      ) : filteredAndSortedUsers.length === 0 ? (
        <div className={styles['no-data']}>
          <p>No users match your search criteria.</p>
          <button type="button" className={styles['clear-filter-button']} onClick={onClearSearch}>
            Clear Search
          </button>
        </div>
      ) : (
        <div className={styles['table-wrapper']}>
          <table className={styles['users-table']}>
            <thead>
              <tr>
                <SortableHeader
                  columnKey="name"
                  label="Name"
                  extraClass="name-sortable-header"
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
                <SortableHeader
                  columnKey="email"
                  label="Email"
                  extraClass="email-sortable-header"
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
                <th scope="col" className={styles['actions-header']}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedUsers.map((user, index) => {
                const chartLoading = Boolean(isChartLoading[user.username]);
                const deleteBusy = deleteSubmittingUsername === user.username;
                return (
                  <tr
                    key={user.username || `user-${index}`}
                    className={styles['user-row']}
                    style={{ '--row-index': index }}
                  >
                    <td className={styles['name-cell']}>
                      <div className={styles['user-info']}>
                        <span className={styles['user-name']}>{user.name}</span>
                      </div>
                    </td>
                    <td className={styles['email-cell']}>
                      <span className={styles['user-email']}>{user.email || '—'}</span>
                    </td>
                    <td className={styles['actions-cell']}>
                      <div className={styles['action-buttons']}>
                        <button
                          type="button"
                          className={styles['dashboard-button']}
                          onClick={() => onDashboardClick(user)}
                        >
                          <ActionIcon name="dashboard" />
                          Dashboard Data
                        </button>
                        <button
                          type="button"
                          className={styles['chart-detail-button']}
                          onClick={() => onChartDetailClick(user)}
                          disabled={chartLoading}
                        >
                          {chartLoading ? (
                            <>
                              <div className={styles['button-spinner']} aria-hidden="true" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <ActionIcon name="chart" />
                              Chart Detail
                            </>
                          )}
                        </button>
                        <button type="button" className={styles['detail-button']} onClick={() => onDetailClick(user)}>
                          <ActionIcon name="detail" />
                          Detail
                        </button>
                        {isSuperadmin ? (
                          <button
                            type="button"
                            className={styles['delete-user-button']}
                            onClick={() => onDeleteUserClick(user)}
                            disabled={deleteBusy}
                          >
                            {deleteBusy ? (
                              <>
                                <div className={styles['button-spinner']} aria-hidden="true" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <ActionIcon name="delete" />
                                Delete User
                              </>
                            )}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
});

export default UsersTable;
